import { AppShell, NavLink, Group, Text, UnstyledButton, Divider, Burger } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import {
  IconDashboard,
  IconBook,
  IconVideo,
  IconUsers,
  IconArticle,
  IconSchool,
  IconLogout,
  IconHome,
  IconCreditCard,
  IconSettings,
  IconShieldLock,
  IconCategory,
} from '@tabler/icons-react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { formatName } from '@/lib/format'
import type { AdminPermissionKey } from '@/constants/permissions'

const navItems: { label: string; icon: typeof IconDashboard; path: string; permission: AdminPermissionKey }[] = [
  { label: 'Хянах самбар', icon: IconDashboard, path: '/', permission: 'dashboard' },
  { label: 'Нүүр хуудас', icon: IconHome, path: '/home-config', permission: 'home_config' },
  { label: 'Сургалтууд', icon: IconBook, path: '/courses', permission: 'courses' },
  { label: 'Видео', icon: IconVideo, path: '/videos', permission: 'videos' },
  { label: 'Багш нар', icon: IconSchool, path: '/teachers', permission: 'teachers' },
  { label: 'Нийтлэлүүд', icon: IconArticle, path: '/posts', permission: 'posts' },
  { label: 'Хэрэглэгчид', icon: IconUsers, path: '/users', permission: 'users' },
  { label: 'Төлбөрүүд', icon: IconCreditCard, path: '/payments', permission: 'payments' },
  { label: 'Төлбөрийн тохиргоо', icon: IconSettings, path: '/payment-settings', permission: 'payment_settings' },
  { label: 'Ангилал тохиргоо', icon: IconCategory, path: '/categories', permission: 'categories' },
  { label: 'Системийн хэрэглэгчид', icon: IconShieldLock, path: '/system-users', permission: 'system_users' },
]

export default function AdminLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout, hasPermission } = useAuth()
  const [mobileOpen, { toggle: toggleMobile, close: closeMobile }] = useDisclosure(false)

  const visibleItems = navItems.filter((item) => hasPermission(item.permission))

  const handleNav = (path: string) => {
    navigate(path)
    closeMobile()
  }

  return (
    <AppShell
      navbar={{ width: 250, breakpoint: 'sm', collapsed: { mobile: !mobileOpen } }}
      header={{ height: { base: 56, sm: 0 } }}
      padding="md"
    >
      <AppShell.Header hiddenFrom="sm" px="md" style={{ display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
        <Burger opened={mobileOpen} onClick={toggleMobile} size="sm" />
        <img src="/logo.png" alt="ARYA Admin" style={{ height: 28 }} />
      </AppShell.Header>

      <AppShell.Navbar p="md">
        <Group mb="md" visibleFrom="sm">
          <img src="/logo.png" alt="ARYA Admin" style={{ height: 36 }} />
        </Group>
        <Divider mb="sm" visibleFrom="sm" />

        {visibleItems.map((item) => (
          <NavLink
            key={item.path}
            label={item.label}
            leftSection={<item.icon size={20} />}
            active={
              item.path === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(item.path)
            }
            onClick={() => handleNav(item.path)}
            mb={4}
          />
        ))}

        <div style={{ flex: 1 }} />
        <Divider mb="sm" />
        <Text size="sm" c="dimmed" mb="xs">{formatName(user?.fullName)}</Text>
        <UnstyledButton
          onClick={() => {
            logout()
            navigate('/login')
          }}
        >
          <Group gap="xs">
            <IconLogout size={18} />
            <Text size="sm">Гарах</Text>
          </Group>
        </UnstyledButton>
      </AppShell.Navbar>

      <AppShell.Main>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <Outlet />
        </div>
      </AppShell.Main>
    </AppShell>
  )
}
